import { Link } from 'react-router-dom'
import {
  ArrowRight, Check, Globe2, CalendarDays, MessageSquareText,
  ShieldCheck, Palette, BellRing, Zap, BarChart2,
} from 'lucide-react'
import { BrandLogo } from '@/components/BrandLogo'
import { ThemeToggle } from '@/components/ThemeToggle'
import { PwaInstallButton } from '@/components/PwaInstallButton'

const APP_URL = import.meta.env.VITE_APP_URL ?? '/login'

/* ─── data ──────────────────────────────────────────────────────────── */
const steps = [
  {
    n: '01', title: 'Connect your website',
    body: 'Somicast verifies ownership, crawls pages, extracts brand colors, logo, services, and business facts.',
    icon: Globe2,
  },
  {
    n: '02', title: 'Set platforms and rhythm',
    body: 'Choose posting frequency, content mix, tone, and target platforms. The planner fills four weeks automatically.',
    icon: CalendarDays,
  },
  {
    n: '03', title: 'Review, approve, post',
    body: 'Every draft routes through your queue. Approve, edit, reject, or regenerate — then schedule manual posting.',
    icon: Check,
  },
]

const faqs = [
  ['Does it write from my website?', 'Yes. Somicast plans and generates from extracted website content, selected platforms, and your posting settings. No blank prompts.'],
  ['Can I review posts before they go out?', 'Yes. Manual review is the default. Every draft lands in an approval queue — approve, edit, reject, or regenerate before anything reaches the calendar.'],
  ['Does it publish automatically?', 'Not in V1. Somicast creates publishing-ready content. Integrations with social APIs are planned for a later module.'],
  ['Do I need to connect my social accounts?', 'No. In V1 you select platforms to shape the content format — no OAuth required.'],
  ['What about images?', 'Image posts get visual prompts that avoid readable text, fake logos, and AI-generated screenshots. Brand colors and templates are applied per website.'],
  ['How does it stay on-brand?', 'Logo, colors, business name, and tone are extracted during onboarding and stored per website. You can override any field later.'],
]

const comparisonRows = [
  ['Understands your website', 'Manual research', 'Needs pasted context', 'No', 'Crawls and learns it'],
  ['Generates ideas automatically', 'You plan it', 'Prompt by prompt', 'No', 'Built into the workflow'],
  ['Keeps content brand-aligned', 'Depends on the person', 'Inconsistent', 'No brand memory', 'Voice and visuals stay consistent'],
  ['Multi-platform workflows', 'Manual resizing', 'Copy/paste heavy', 'Scheduling only', 'Platform-aware drafts'],
  ['Reduces manual content work', 'Low', 'Medium', 'Medium', 'High'],
  ['Built for ongoing marketing', 'Hard to sustain', 'One-off outputs', 'Calendar only', 'Continuous engine'],
]

/* ─── Dashboard mockup ──────────────────────────────────────────────── */
function DashboardMockup() {
  return (
    <div className="scx-mock-shell" aria-label="Somicast dashboard preview" role="img">
      <div className="scx-mock-chrome">
        <span className="scx-mock-orb" />
        <span className="scx-mock-orb" />
        <span className="scx-mock-orb" />
        <div className="scx-mock-url">somicast.com/dashboard</div>
      </div>

      <div className="scx-mock-app">
        {/* sidebar */}
        <div className="scx-mock-sidebar">
          <div className="scx-mock-sidebar-logo">
            <div className="scx-mock-logo-mark" />
            <div>
              <div className="scx-bar" style={{ width: 58, height: 7, marginBottom: 4 }} />
              <div className="scx-bar" style={{ width: 38, height: 5, opacity: .4 }} />
            </div>
          </div>
          {['Dashboard', 'Content Queue', 'Calendar', 'Websites', 'Settings'].map((item, i) => (
            <div key={item} className={`scx-mock-nav-item${i === 1 ? ' active' : ''}`}>
              <div className="scx-mock-nav-icon" />
              <div className="scx-bar" style={{ width: 52 + (i % 3) * 16, height: 7 }} />
            </div>
          ))}
        </div>

        {/* main */}
        <div className="scx-mock-main">
          <div className="scx-mock-topbar">
            <div>
              <div className="scx-bar" style={{ width: 100, height: 10, marginBottom: 6 }} />
              <div className="scx-bar" style={{ width: 170, height: 6, opacity: .4 }} />
            </div>
            <div className="scx-mock-topbar-r">
              <div className="scx-mock-badge">5 pending</div>
              <div className="scx-mock-avatar" />
            </div>
          </div>

          <div className="scx-mock-kpis">
            {[['12', 'Queued'], ['5', 'Review'], ['Mon', 'Next post']].map(([val, lbl]) => (
              <div key={lbl} className="scx-mock-kpi">
                <div className="scx-mock-kpi-val">{val}</div>
                <div className="scx-bar" style={{ width: '65%', height: 6, marginTop: 7, opacity: .4 }} />
              </div>
            ))}
          </div>

          <div className="scx-mock-section-lbl">Content Queue</div>
          <div className="scx-mock-queue">
            {[
              { platform: 'LinkedIn', type: 'Text', status: 'pending', topic: 'Why most marketing calendars stay empty and what to do instead' },
              { platform: 'Facebook', type: 'Photo', status: 'pending', topic: 'Show the product benefit without a hard sell — service highlight' },
              { platform: 'Twitter', type: 'Thread', status: 'approved', topic: 'Quick answer to the most common question about your pricing' },
              { platform: 'TikTok', type: 'Script', status: 'pending', topic: 'Behind the scenes: how a client found us through a single blog post' },
            ].map(({ platform, type, status, topic }) => (
              <div key={topic} className="scx-mock-row">
                <div className="scx-mock-platform-dot" />
                <div className="scx-mock-row-body">
                  <div className="scx-mock-row-meta">{type} · {platform}</div>
                  <div className="scx-mock-row-topic">{topic}</div>
                </div>
                <div className={`scx-mock-status ${status}`}>{status === 'approved' ? '✓ Ready' : 'Review'}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ─── Bento visuals ─────────────────────────────────────────────────── */
function ScheduleVisual() {
  const days = ['M', 'T', 'W', 'T', 'F']
  const filled = [1, 0, 1, 1, 0]
  return (
    <div className="scx-bento-visual scx-sched-visual">
      {days.map((d, i) => (
        <div key={i} className="scx-sched-col">
          <div className="scx-sched-lbl">{d}</div>
          <div className={`scx-sched-block${filled[i] ? ' filled' : ''}`}>
            {filled[i] ? <Zap size={11} /> : null}
          </div>
        </div>
      ))}
    </div>
  )
}

function BrandVisual() {
  return (
    <div className="scx-bento-visual scx-brand-visual">
      <div className="scx-brand-rings">
        <div className="scx-brand-ring r1">
          <div className="scx-brand-ring r2">
            <div className="scx-brand-core" />
          </div>
        </div>
      </div>
      <div className="scx-brand-tags">
        {['Logo', 'Colors', 'Tone', 'Templates'].map(t => (
          <span key={t} className="scx-brand-tag">{t}</span>
        ))}
      </div>
    </div>
  )
}

function ReviewVisual() {
  return (
    <div className="scx-bento-visual">
      <div className="scx-review-card">
        <div className="scx-review-lines">
          <div className="scx-bar" style={{ width: '90%', height: 7 }} />
          <div className="scx-bar" style={{ width: '75%', height: 7 }} />
          <div className="scx-bar" style={{ width: '55%', height: 7 }} />
        </div>
        <div className="scx-review-actions">
          <span className="scx-review-btn approve"><Check size={10} /> Approve</span>
          <span className="scx-review-btn edit">Edit</span>
          <span className="scx-review-btn reject">Reject</span>
        </div>
      </div>
    </div>
  )
}

function PlatformVisual() {
  const platforms = ['LinkedIn', 'Facebook', 'X / Twitter', 'TikTok', 'YouTube', 'Instagram']
  return (
    <div className="scx-bento-visual scx-platform-visual">
      {platforms.map(name => (
        <span key={name} className="scx-platform-chip">{name}</span>
      ))}
    </div>
  )
}

const bentoFeatures = [
  { icon: Globe2,           title: 'Website-first content',   body: 'Every post traces back to real pages, extracted facts, services, and offers — not blank AI guesses.',                             size: 'wide',   Visual: null },
  { icon: CalendarDays,     title: 'Four-week planner',        body: 'Missing slots get filled automatically with days, topics, formats, and platform targets.',                                         size: 'tall',   Visual: ScheduleVisual },
  { icon: Palette,          title: 'Brand intelligence',       body: 'Logo, colors, voice, and templates stay attached to every website.',                                                               size: 'tall',   Visual: BrandVisual },
  { icon: MessageSquareText,title: 'Review queue',             body: 'Approve, edit, reject with reason, or regenerate. Every draft reviewed before posting.',                                          size: 'wide',   Visual: ReviewVisual },
  { icon: ShieldCheck,      title: 'Platform-aware output',    body: 'LinkedIn text, TikTok scripts, Facebook photo posts — each shaped for where it will be seen.',                                    size: 'normal', Visual: PlatformVisual },
  { icon: BellRing,         title: 'Planner health checks',    body: 'Dashboard flags missing setup, platform gaps, and schedule issues before empty weeks happen.',                                    size: 'normal', Visual: null },
  { icon: BarChart2,        title: 'Learning loop',            body: 'Approvals and rejections become structured signals that shape better future drafts.',                                             size: 'normal', Visual: null },
]

/* ─── Page ──────────────────────────────────────────────────────────── */
export default function Landing() {
  return (
    <main className="scx-page">

      {/* Nav */}
      <header className="scx-nav">
        <Link to="/" className="scx-brand" aria-label="Somicast home">
          <BrandLogo className="scx-logo" />
          <span>Somicast</span>
        </Link>
        <nav className="scx-links" aria-label="Main navigation">
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#comparison">Compare</a>
          <a href="#faq">FAQ</a>
        </nav>
        <div className="scx-actions">
          <PwaInstallButton compact className="scx-install" />
          <ThemeToggle compact />
          <Link to="/login" className="scx-nav-ghost">Log in</Link>
          <Link to={APP_URL} className="scx-nav-primary">Start free <ArrowRight size={14} /></Link>
        </div>
      </header>

      {/* Hero */}
      <section className="scx-hero">
        <div className="scx-hero-inner">
          <h1>
            Your website is already<br />
            your best content team.
          </h1>
          <p className="scx-sub">
            Somicast crawls your site, extracts brand intelligence, plans four weeks of posts,
            routes every draft through approval, and prepares manual sharing — without a single prompt.
          </p>
          <div className="scx-hero-ctas">
            <Link to={APP_URL} className="scx-hero-primary">
              Start free <ArrowRight size={15} />
            </Link>
            <a href="#how" className="scx-hero-ghost">See how it works</a>
          </div>
          <div className="scx-trust-row">
            <span>No social login required</span>
            <i />
            <span>Manual review before every post</span>
            <i />
            <span>Platform-aware drafts</span>
          </div>
        </div>

        <div className="scx-hero-mockup-wrap">
          <DashboardMockup />
        </div>
      </section>

      {/* Stats strip */}
      <div className="scx-stats-strip" aria-label="Somicast at a glance">
        {[
          ['100+', 'pages crawled per site'],
          ['4 weeks', 'planned automatically'],
          ['6', 'platform formats'],
          ['1 queue', 'full approval control'],
        ].map(([val, label]) => (
          <div key={label} className="scx-stat">
            <strong>{val}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>

      {/* How it works */}
      <section className="scx-section" id="how">
        <div className="scx-section-head">
          <h2>From website to content engine in three steps.</h2>
          <p>No spreadsheets. No prompts. Somicast turns what you already have into a weekly content workflow.</p>
        </div>
        <div className="scx-steps">
          {steps.map(({ n, title, body, icon: Icon }, i) => (
            <div key={n} className="scx-step-group">
              <div className="scx-step-card">
                <div className="scx-step-icon-wrap"><Icon size={20} /></div>
                <div className="scx-step-num">{n}</div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
              {i < steps.length - 1 && (
                <div className="scx-step-connector" aria-hidden>
                  <div className="scx-step-line" />
                  <ArrowRight size={13} />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Bento features */}
      <section className="scx-section scx-alt" id="features">
        <div className="scx-section-head">
          <h2>Built for people who just want posting handled.</h2>
          <p>Every piece is connected — from crawl to brand to drafts to calendar.</p>
        </div>
        <div className="scx-bento">
          {bentoFeatures.map(({ icon: Icon, title, body, size, Visual }) => (
            <article key={title} className={`scx-bento-card scx-bento-${size}`}>
              <div className="scx-bento-icon"><Icon size={18} /></div>
              <h3>{title}</h3>
              <p>{body}</p>
              {Visual && <Visual />}
            </article>
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="scx-section" id="comparison">
        <div className="scx-section-head">
          <h2>One workflow. No context switching.</h2>
          <p>Manual posting, AI tools, and schedulers solve one piece each. Somicast connects them all.</p>
        </div>
        <div className="scx-compare-wrap">
          <div className="scx-compare-table" role="table" aria-label="Somicast vs alternatives">
            <div className="scx-compare-row scx-compare-head" role="row">
              {['Feature', 'Manual posting', 'AI writing tools', 'Schedulers', 'Somicast'].map(h => (
                <div role="columnheader" key={h}>{h}</div>
              ))}
            </div>
            {comparisonRows.map(([feature, ...rest]) => (
              <div className="scx-compare-row" role="row" key={feature}>
                <div className="scx-compare-feature" role="cell" data-label="Feature">{feature}</div>
                {rest.slice(0, 3).map((cell, i) => (
                  <div role="cell" key={i} data-label={['Manual posting', 'AI writing tools', 'Schedulers'][i]}>{cell}</div>
                ))}
                <div className="scx-compare-win" role="cell" data-label="Somicast">
                  <Check size={12} /> {rest[3]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="scx-section scx-alt" id="faq">
        <div className="scx-section-head">
          <h2>Simple answers before you start.</h2>
        </div>
        <div className="scx-faq-grid">
          {faqs.map(([q, a]) => (
            <details key={q} className="scx-faq">
              <summary>{q}</summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="scx-cta-section">
        <h2>Your site already has the content.<br />We turn it into posts.</h2>
        <p>Connect a site, pick your platforms, and let Somicast build and fill the plan.</p>
        <Link to={APP_URL} className="scx-cta-btn">
          Start free <ArrowRight size={16} />
        </Link>
      </section>

      {/* Footer */}
      <footer className="scx-footer">
        <div className="scx-footer-brand-col">
          <Link to="/" className="scx-footer-brand">
            <BrandLogo className="scx-logo" />
            <strong>Somicast</strong>
          </Link>
          <p>Simple social content from the website you already have. Connect, review, publish, learn.</p>
        </div>
        <nav className="scx-footer-col">
          <strong>Product</strong>
          <a href="#how">How it works</a>
          <a href="#features">Features</a>
          <a href="#comparison">Compare</a>
        </nav>
        <nav className="scx-footer-col">
          <strong>Account</strong>
          <Link to="/login">Log in</Link>
          <Link to={APP_URL}>Start free</Link>
        </nav>
        <nav className="scx-footer-col">
          <strong>Company</strong>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <a href="mailto:support@somicast.com">Support</a>
        </nav>
      </footer>

      {/* ── Styles ── */}
      <style>{`
        /* ── Page tokens ── */
        .scx-page {
          --p-bg:      #f4f4f3;
          --p-bg2:     #ebebea;
          --p-panel:   #ffffff;
          --p-surface: #f9f9f8;
          --p-border:  rgba(0,0,0,.10);
          --p-border2: rgba(0,0,0,.18);
          --p-text:    #0d0d0d;
          --p-text2:   #1a1a1a;
          --p-dim:     #4a4a4a;
          --p-muted:   #888888;
          --p-edge:    max(16px, calc((100vw - 1160px) / 2));
          min-height: 100vh;
          background: var(--p-bg);
          color: var(--p-text);
          font-family: "Plus Jakarta Sans", var(--font-sans);
          overflow-x: hidden;
        }
        .dark .scx-page {
          --p-bg:      #0a0a0a;
          --p-bg2:     #111111;
          --p-panel:   #141414;
          --p-surface: #1a1a1a;
          --p-border:  rgba(255,255,255,.10);
          --p-border2: rgba(255,255,255,.18);
          --p-text:    #f0f0f0;
          --p-text2:   #e0e0e0;
          --p-dim:     #999999;
          --p-muted:   #555555;
        }
        .scx-page * { box-sizing: border-box; }

        /* ── Nav ── */
        .scx-nav {
          position: fixed;
          inset: 0 0 auto;
          height: 60px;
          z-index: 50;
          display: grid;
          grid-template-columns: auto 1fr auto;
          align-items: center;
          gap: 20px;
          padding: 0 var(--p-edge);
          background: color-mix(in srgb, var(--p-panel) 90%, transparent);
          border-bottom: 1px solid var(--p-border);
          backdrop-filter: blur(16px);
          -webkit-backdrop-filter: blur(16px);
        }
        .scx-brand {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          color: var(--p-text);
          text-decoration: none;
          font-weight: 800;
          font-size: 15px;
        }
        .scx-logo {
          width: 30px; height: 30px;
          border-radius: 6px;
          display: block;
          flex-shrink: 0;
          object-fit: contain;
        }
        .scx-links {
          display: flex;
          justify-content: center;
          gap: 2px;
        }
        .scx-links a {
          color: var(--p-dim);
          text-decoration: none;
          font-size: 13.5px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 6px;
          transition: color .12s, background .12s;
        }
        .scx-links a:hover { color: var(--p-text); background: var(--p-bg2); }
        .scx-actions { display: flex; align-items: center; gap: 8px; }
        .scx-nav-ghost {
          color: var(--p-dim);
          text-decoration: none;
          font-size: 13.5px;
          font-weight: 600;
          padding: 6px 12px;
          border-radius: 6px;
          transition: color .12s, background .12s;
        }
        .scx-nav-ghost:hover { color: var(--p-text); background: var(--p-bg2); }
        .scx-nav-primary {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          color: var(--p-bg);
          background: var(--p-text);
          border-radius: 7px;
          padding: 8px 16px;
          text-decoration: none;
          font-weight: 700;
          font-size: 13.5px;
          transition: opacity .12s;
        }
        .scx-nav-primary:hover { opacity: .78; }

        /* ── Hero ── */
        .scx-hero {
          padding-top: 100px;
          display: flex;
          flex-direction: column;
          align-items: center;
          border-bottom: 1px solid var(--p-border);
          background: var(--p-bg);
        }
        .scx-hero-inner {
          text-align: center;
          max-width: 760px;
          padding: 0 24px 52px;
        }
        .scx-hero h1 {
          margin: 0 0 22px;
          font-size: clamp(36px, 5vw, 64px);
          line-height: 1.04;
          letter-spacing: -.03em;
          font-weight: 900;
          color: var(--p-text);
        }
        .scx-sub {
          max-width: 600px;
          margin: 0 auto 32px;
          color: var(--p-dim);
          font-size: 17px;
          line-height: 1.75;
        }
        .scx-hero-ctas {
          display: flex;
          gap: 12px;
          justify-content: center;
          flex-wrap: wrap;
          margin-bottom: 28px;
        }
        .scx-hero-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: var(--p-bg);
          background: var(--p-text);
          border-radius: 9px;
          padding: 13px 28px;
          text-decoration: none;
          font-weight: 800;
          font-size: 15px;
          transition: opacity .12s;
        }
        .scx-hero-primary:hover { opacity: .78; }
        .scx-hero-ghost {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: var(--p-dim);
          border: 1px solid var(--p-border2);
          border-radius: 9px;
          padding: 13px 24px;
          text-decoration: none;
          font-weight: 700;
          font-size: 15px;
          transition: color .12s, background .12s;
        }
        .scx-hero-ghost:hover { color: var(--p-text); background: var(--p-bg2); }
        .scx-trust-row {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 16px;
          flex-wrap: wrap;
          color: var(--p-muted);
          font-size: 12.5px;
          font-weight: 600;
        }
        .scx-trust-row i {
          width: 1px;
          height: 14px;
          background: var(--p-border2);
          display: block;
          flex-shrink: 0;
        }

        /* ── Hero mockup ── */
        .scx-hero-mockup-wrap {
          width: min(100%, 1100px);
          padding: 0 16px;
        }
        .scx-mock-shell {
          background: var(--p-panel);
          border: 1px solid var(--p-border2);
          border-radius: 12px 12px 0 0;
          overflow: hidden;
        }
        .scx-mock-chrome {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 16px;
          background: var(--p-bg2);
          border-bottom: 1px solid var(--p-border);
        }
        .scx-mock-orb {
          width: 10px; height: 10px;
          border-radius: 50%;
          background: var(--p-border2);
          flex-shrink: 0;
        }
        .scx-mock-url {
          flex: 1;
          text-align: center;
          font-size: 11.5px;
          color: var(--p-muted);
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 5px;
          padding: 4px 10px;
          max-width: 300px;
          margin: 0 auto;
        }
        .scx-mock-app {
          display: grid;
          grid-template-columns: 196px 1fr;
        }
        .scx-mock-sidebar {
          border-right: 1px solid var(--p-border);
          padding: 14px 10px;
          background: var(--p-surface);
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .scx-mock-sidebar-logo {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 4px 8px 12px;
          border-bottom: 1px solid var(--p-border);
          margin-bottom: 8px;
        }
        .scx-mock-logo-mark {
          width: 24px; height: 24px;
          border-radius: 6px;
          background: var(--p-text);
          flex-shrink: 0;
        }
        .scx-mock-nav-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 7px 10px;
          border-radius: 7px;
        }
        .scx-mock-nav-item.active { background: var(--p-bg2); }
        .scx-mock-nav-icon {
          width: 13px; height: 13px;
          border-radius: 3px;
          background: var(--p-border2);
          flex-shrink: 0;
        }
        .scx-mock-nav-item.active .scx-mock-nav-icon { background: var(--p-text); opacity: .5; }
        .scx-mock-main {
          padding: 18px;
          background: var(--p-bg);
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .scx-mock-topbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .scx-mock-topbar-r { display: flex; align-items: center; gap: 9px; }
        .scx-mock-badge {
          font-size: 11px;
          font-weight: 700;
          color: var(--p-text);
          background: var(--p-bg2);
          border: 1px solid var(--p-border2);
          border-radius: 999px;
          padding: 3px 10px;
        }
        .scx-mock-avatar {
          width: 26px; height: 26px;
          border-radius: 50%;
          background: var(--p-border2);
        }
        .scx-mock-kpis {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .scx-mock-kpi {
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 9px;
          padding: 12px;
        }
        .scx-mock-kpi-val {
          font-size: 18px;
          font-weight: 900;
          color: var(--p-text);
        }
        .scx-mock-section-lbl {
          font-size: 10.5px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: .09em;
          color: var(--p-muted);
        }
        .scx-mock-queue {
          display: flex;
          flex-direction: column;
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 9px;
          overflow: hidden;
        }
        .scx-mock-row {
          display: grid;
          grid-template-columns: 8px 1fr auto;
          align-items: center;
          gap: 11px;
          padding: 10px 13px;
          border-bottom: 1px solid var(--p-border);
        }
        .scx-mock-row:last-child { border-bottom: 0; }
        .scx-mock-platform-dot {
          width: 7px; height: 7px;
          border-radius: 50%;
          background: var(--p-border2);
          flex-shrink: 0;
        }
        .scx-mock-row-meta {
          font-size: 10px;
          font-weight: 700;
          color: var(--p-muted);
          text-transform: uppercase;
          letter-spacing: .06em;
          margin-bottom: 3px;
        }
        .scx-mock-row-topic {
          font-size: 12px;
          color: var(--p-text2);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 420px;
        }
        .scx-mock-status {
          font-size: 10.5px;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 999px;
          border: 1px solid var(--p-border2);
          background: var(--p-bg2);
          color: var(--p-dim);
          white-space: nowrap;
          flex-shrink: 0;
        }
        .scx-mock-status.approved {
          color: var(--p-text);
          border-color: var(--p-text);
          background: transparent;
        }

        /* shared bar for skeleton lines */
        .scx-bar {
          height: 8px;
          border-radius: 999px;
          background: var(--p-border2);
          display: block;
        }

        /* ── Stats strip ── */
        .scx-stats-strip {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          background: var(--p-bg2);
          border-bottom: 1px solid var(--p-border);
        }
        .scx-stat {
          padding: 26px 20px;
          text-align: center;
          border-right: 1px solid var(--p-border);
        }
        .scx-stat:last-child { border-right: none; }
        .scx-stat strong {
          display: block;
          font-size: 22px;
          font-weight: 900;
          color: var(--p-text);
          margin-bottom: 4px;
        }
        .scx-stat span {
          font-size: 11.5px;
          color: var(--p-muted);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: .09em;
        }

        /* ── Section ── */
        .scx-section {
          padding: 80px var(--p-edge);
          border-bottom: 1px solid var(--p-border);
        }
        .scx-alt { background: var(--p-bg2); }
        .scx-section-head {
          max-width: 660px;
          margin: 0 auto 52px;
          text-align: center;
        }
        .scx-section h2 {
          font-size: clamp(26px, 3.4vw, 44px);
          font-weight: 900;
          line-height: 1.1;
          letter-spacing: -.025em;
          margin: 0 0 14px;
          color: var(--p-text);
        }
        .scx-section-head p {
          color: var(--p-dim);
          font-size: 16px;
          line-height: 1.75;
          margin: 0;
        }

        /* ── Steps ── */
        .scx-steps {
          max-width: 1000px;
          margin: 0 auto;
          display: flex;
          align-items: stretch;
        }
        .scx-step-group {
          display: flex;
          align-items: center;
          flex: 1;
          min-width: 0;
        }
        .scx-step-card {
          flex: 1;
          min-width: 0;
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 14px;
          padding: 26px 22px;
          position: relative;
        }
        .scx-step-icon-wrap {
          width: 40px; height: 40px;
          border-radius: 10px;
          border: 1px solid var(--p-border2);
          color: var(--p-text);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }
        .scx-step-num {
          position: absolute;
          top: 16px; right: 18px;
          font-size: 11px;
          font-weight: 900;
          color: var(--p-muted);
          letter-spacing: .06em;
        }
        .scx-step-card h3 {
          margin: 0 0 8px;
          font-size: 16px;
          font-weight: 800;
          color: var(--p-text);
        }
        .scx-step-card p {
          margin: 0;
          font-size: 14px;
          color: var(--p-dim);
          line-height: 1.65;
        }
        .scx-step-connector {
          display: flex;
          align-items: center;
          gap: 0;
          padding: 0 6px;
          color: var(--p-muted);
          flex-shrink: 0;
        }
        .scx-step-line {
          width: 20px;
          height: 1px;
          background: repeating-linear-gradient(
            90deg, var(--p-border2) 0, var(--p-border2) 3px,
            transparent 3px, transparent 8px
          );
        }

        /* ── Bento ── */
        .scx-bento {
          max-width: 1160px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 10px;
        }
        .scx-bento-card {
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 14px;
          padding: 22px;
          display: flex;
          flex-direction: column;
          gap: 9px;
          overflow: hidden;
        }
        .scx-bento-wide   { grid-column: span 4; }
        .scx-bento-tall   { grid-column: span 2; }
        .scx-bento-normal { grid-column: span 2; }

        .scx-bento-icon {
          width: 38px; height: 38px;
          border-radius: 9px;
          border: 1px solid var(--p-border2);
          color: var(--p-text);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .scx-bento-card h3 {
          margin: 0;
          font-size: 15.5px;
          font-weight: 800;
          color: var(--p-text);
        }
        .scx-bento-card p {
          margin: 0;
          font-size: 13.5px;
          color: var(--p-dim);
          line-height: 1.65;
        }
        .scx-bento-visual { margin-top: auto; padding-top: 14px; }

        /* schedule visual */
        .scx-sched-visual { display: flex; gap: 8px; align-items: flex-end; }
        .scx-sched-col { display: flex; flex-direction: column; align-items: center; gap: 5px; flex: 1; }
        .scx-sched-lbl {
          font-size: 10.5px; font-weight: 700;
          color: var(--p-muted); text-transform: uppercase;
        }
        .scx-sched-block {
          width: 100%; height: 60px;
          border-radius: 7px;
          border: 1px dashed var(--p-border2);
          background: var(--p-bg2);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--p-muted);
        }
        .scx-sched-block.filled {
          border-style: solid;
          border-color: var(--p-text);
          background: var(--p-bg2);
          color: var(--p-text);
        }

        /* brand visual */
        .scx-brand-visual { display: flex; align-items: center; gap: 16px; }
        .scx-brand-rings { display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .scx-brand-ring {
          border-radius: 50%;
          border: 1px solid var(--p-border2);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .r1 { width: 68px; height: 68px; }
        .r2 { width: 44px; height: 44px; }
        .scx-brand-core {
          width: 20px; height: 20px;
          border-radius: 50%;
          background: var(--p-text);
        }
        .scx-brand-tags { display: flex; flex-wrap: wrap; gap: 5px; }
        .scx-brand-tag {
          font-size: 11.5px;
          font-weight: 700;
          color: var(--p-dim);
          border: 1px solid var(--p-border2);
          border-radius: 999px;
          padding: 3px 9px;
        }

        /* review visual */
        .scx-review-card {
          background: var(--p-bg2);
          border: 1px solid var(--p-border);
          border-radius: 10px;
          padding: 14px;
        }
        .scx-review-lines {
          display: flex; flex-direction: column; gap: 7px; margin-bottom: 14px;
        }
        .scx-review-actions { display: flex; gap: 7px; }
        .scx-review-btn {
          font-size: 11.5px;
          font-weight: 700;
          padding: 5px 11px;
          border-radius: 6px;
          border: 1px solid var(--p-border2);
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: var(--p-dim);
        }
        .scx-review-btn.approve { color: var(--p-text); border-color: var(--p-text); }

        /* platform visual */
        .scx-platform-visual { display: flex; flex-wrap: wrap; gap: 6px; padding-top: 10px; }
        .scx-platform-chip {
          font-size: 11.5px;
          font-weight: 700;
          color: var(--p-dim);
          border: 1px solid var(--p-border2);
          border-radius: 999px;
          padding: 4px 11px;
        }

        /* ── Comparison ── */
        .scx-compare-wrap { max-width: 1160px; margin: 0 auto; }
        .scx-compare-table {
          border: 1px solid var(--p-border);
          border-radius: 14px;
          overflow: hidden;
          background: var(--p-panel);
        }
        .scx-compare-row {
          display: grid;
          grid-template-columns: 1.4fr repeat(4, minmax(0,1fr));
          min-height: 58px;
          border-bottom: 1px solid var(--p-border);
        }
        .scx-compare-row:last-child { border-bottom: 0; }
        .scx-compare-row > div {
          padding: 13px 15px;
          display: flex;
          align-items: center;
          border-right: 1px solid var(--p-border);
          color: var(--p-dim);
          font-size: 13px;
          line-height: 1.45;
        }
        .scx-compare-row > div:last-child { border-right: 0; }
        .scx-compare-head {
          background: var(--p-bg2);
          min-height: 44px;
        }
        .scx-compare-head > div {
          color: var(--p-text);
          font-weight: 800;
          font-size: 11.5px;
          text-transform: uppercase;
          letter-spacing: .07em;
        }
        .scx-compare-feature { color: var(--p-text) !important; font-weight: 800; }
        .scx-compare-win {
          color: var(--p-text) !important;
          font-weight: 700;
          gap: 8px;
        }
        .scx-compare-win svg { flex-shrink: 0; }

        /* ── FAQ ── */
        .scx-faq-grid {
          max-width: 900px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
        }
        .scx-faq {
          background: var(--p-panel);
          border: 1px solid var(--p-border);
          border-radius: 12px;
          overflow: hidden;
        }
        .scx-faq summary {
          cursor: pointer;
          padding: 18px 20px;
          color: var(--p-text);
          font-weight: 800;
          font-size: 14px;
          line-height: 1.4;
          list-style: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          user-select: none;
        }
        .scx-faq summary::-webkit-details-marker { display: none; }
        .scx-faq summary::after {
          content: '+';
          font-size: 20px;
          font-weight: 300;
          color: var(--p-muted);
          flex-shrink: 0;
          line-height: 1;
          transition: transform .2s;
        }
        .scx-faq[open] summary::after { transform: rotate(45deg); }
        .scx-faq p {
          color: var(--p-dim);
          padding: 14px 20px 20px;
          margin: 0;
          font-size: 13.5px;
          line-height: 1.75;
          border-top: 1px solid var(--p-border);
        }

        /* ── CTA ── */
        .scx-cta-section {
          padding: 96px var(--p-edge);
          text-align: center;
          background: var(--p-text);
          color: var(--p-bg);
        }
        .scx-cta-section h2 {
          font-size: clamp(26px, 3.4vw, 48px);
          font-weight: 900;
          line-height: 1.1;
          letter-spacing: -.025em;
          margin: 0 0 16px;
          color: var(--p-bg);
        }
        .scx-cta-section p {
          color: color-mix(in srgb, var(--p-bg) 65%, transparent);
          font-size: 16px;
          line-height: 1.7;
          margin: 0 0 36px;
        }
        .scx-cta-btn {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          color: var(--p-text);
          background: var(--p-bg);
          border-radius: 10px;
          padding: 15px 34px;
          text-decoration: none;
          font-weight: 800;
          font-size: 15px;
          transition: opacity .12s;
        }
        .scx-cta-btn:hover { opacity: .82; }

        /* ── Footer ── */
        .scx-footer {
          display: grid;
          grid-template-columns: minmax(260px, 1.7fr) repeat(3, minmax(110px, 1fr));
          gap: 32px;
          padding: 48px var(--p-edge);
          background: var(--p-bg2);
          border-top: 1px solid var(--p-border);
          color: var(--p-dim);
        }
        .scx-footer-brand {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          color: var(--p-text);
          text-decoration: none;
          font-weight: 800;
          margin-bottom: 12px;
        }
        .scx-footer-brand-col p {
          max-width: 360px;
          font-size: 13.5px;
          line-height: 1.75;
          margin: 0;
          color: var(--p-dim);
        }
        .scx-footer-col { display: grid; align-content: start; gap: 10px; }
        .scx-footer-col strong {
          font-size: 12.5px;
          font-weight: 800;
          color: var(--p-text);
          margin-bottom: 3px;
        }
        .scx-footer a {
          color: var(--p-dim);
          text-decoration: none;
          font-size: 13px;
          font-weight: 600;
        }
        .scx-footer a:hover { color: var(--p-text); }

        /* ── Responsive ── */
        @media (max-width: 1100px) {
          .scx-bento-wide { grid-column: span 6; }
          .scx-bento-tall { grid-column: span 3; }
          .scx-bento-normal { grid-column: span 3; }
          .scx-mock-app { grid-template-columns: 170px 1fr; }
          .scx-mock-row-topic { max-width: 260px; }
        }
        @media (max-width: 900px) {
          .scx-nav { grid-template-columns: auto auto; justify-content: space-between; }
          .scx-links { display: none; }
          .scx-nav-ghost { display: none; }
          .scx-steps { flex-direction: column; }
          .scx-step-connector { display: none; }
          .scx-step-group { width: 100%; }
          .scx-step-card { width: 100%; }
          .scx-bento { grid-template-columns: 1fr; }
          .scx-bento-wide, .scx-bento-tall, .scx-bento-normal { grid-column: span 1; }
          .scx-faq-grid { grid-template-columns: 1fr; }
          .scx-compare-row { grid-template-columns: 1fr; }
          .scx-compare-head { display: none; }
          .scx-compare-row > div {
            border-right: 0;
            border-bottom: 1px solid var(--p-border);
            justify-content: space-between;
          }
          .scx-compare-row > div:last-child { border-bottom: 0; }
          .scx-compare-row > div::before {
            content: attr(data-label);
            color: var(--p-muted);
            font-size: 11px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: .07em;
          }
          .scx-compare-feature::before { display: none; }
          .scx-compare-feature { background: var(--p-bg2); }
          .scx-stats-strip { grid-template-columns: repeat(2, 1fr); }
          .scx-stat:nth-child(2n) { border-right: none; }
          .scx-stat:nth-child(n+3) { border-top: 1px solid var(--p-border); }
          .scx-footer { grid-template-columns: 1fr 1fr 1fr; }
          .scx-footer-brand-col { grid-column: 1 / -1; }
          .scx-mock-sidebar { display: none; }
          .scx-mock-app { grid-template-columns: 1fr; }
        }
        @media (max-width: 640px) {
          .scx-section { padding: 60px 16px; }
          .scx-hero { padding-top: 80px; }
          .scx-hero-inner { padding-bottom: 32px; }
          .scx-hero h1 { font-size: 34px; letter-spacing: -.02em; }
          .scx-sub { font-size: 15px; }
          .scx-hero-ctas { flex-direction: column; align-items: stretch; }
          .scx-hero-primary, .scx-hero-ghost { justify-content: center; }
          .scx-trust-row { flex-direction: column; gap: 6px; }
          .scx-trust-row i { display: none; }
          .scx-stats-strip { grid-template-columns: 1fr; }
          .scx-stat { border-right: none !important; border-bottom: 1px solid var(--p-border); }
          .scx-stat:last-child { border-bottom: none; }
          .scx-footer { grid-template-columns: 1fr; padding: 36px 16px; }
          .scx-cta-section { padding: 72px 16px; }
          .scx-section-head { margin-bottom: 36px; }
        }
      `}</style>
    </main>
  )
}
